import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, Calendar } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { companyInfo } from '../data/mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const goToHome = () => {
    if (window.location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={goToHome}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_premium-arborcare/artifacts/u250ehvi_print%202%20af.jpg"
              alt="Next Generation Tree Service & Landscaping"
              className="h-14 w-auto object-contain"
            />
            <div className="hidden md:block">
              <h1 className="text-white font-bold text-lg leading-tight">
                Next Generation
              </h1>
              <p className="text-green-400 text-xs">Tree Service & Landscaping</p>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className="text-white hover:text-green-400 transition-colors font-medium"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-white hover:text-green-400 transition-colors font-medium"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="text-white hover:text-green-400 transition-colors font-medium"
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="text-white hover:text-green-400 transition-colors font-medium"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-white hover:text-green-400 transition-colors font-medium"
            >
              Contact
            </button>
            <Link
              to="/book"
              className="text-green-400 hover:text-green-300 transition-colors font-semibold"
            >
              Book Now
            </Link>
          </nav>

          {/* Call Now Button */}
          <div className="flex items-center space-x-4">
            <a href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}>
              <Button className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 hidden sm:flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>Call Now</span>
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-black/98 backdrop-blur-md py-6 space-y-4 border-t border-green-500/20">
            <Link
              to="/book"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-3 text-green-400 hover:text-green-300 hover:bg-green-500/10 transition-colors font-semibold border-b border-green-500/20"
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Book Now
            </Link>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left px-4 py-2 text-white hover:text-green-400 hover:bg-green-500/10 transition-colors"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left px-4 py-2 text-white hover:text-green-400 hover:bg-green-500/10 transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="block w-full text-left px-4 py-2 text-white hover:text-green-400 hover:bg-green-500/10 transition-colors"
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="block w-full text-left px-4 py-2 text-white hover:text-green-400 hover:bg-green-500/10 transition-colors"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-2 text-white hover:text-green-400 hover:bg-green-500/10 transition-colors"
            >
              Contact
            </button>
            <a
              href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}
              className="block mx-4"
            >
              <Button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold">
                <Phone className="w-4 h-4 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
          </div>
        )}
      </div>
    </header>
  );
};
